<template>
    <div>
        <div class="ix-comments" v-bind:class="{ reverse: order == 'reverse' }">
            <div class="ix-comments-content" id="commentsBox">

                <div class="ix-comments-title" v-if="(!comments || comments.length == 0) && !loading">Pas de commentaires, soyez le premier à participer</div>

                <div class="ix-comments-loader" v-if="loading">
                    <i class="far fa-circle-notch fa-spin"></i> Chargement des commentaires
                </div>

                <div class="mb-2" v-if="(pagination.last_page !== null && pagination.last_page > pagination.current_page) && !loading">
                    <button class="ix-btn transparent ix-btn-block" @click="getData(pagination.current_page + 1)">Afficher les commentaires précédents</button>
                </div>

                <div class="ix-comments-list" v-bind:class="{ boxed: restrictHeight !== undefined }" :style="{'max-height': `${restrictHeight}px`}">
                    <div class="ix-comment" v-for="(comment,$index) in comments" :key="$index" v-bind:class="{ self: comment.is_own, 'same-own-next': $index >= 1 && isSameGroup(comment, comments[$index - 1]) }">
                        <div class="ix-comment-left">
                            <span class="ix-avatar xsm" v-if="!comment.is_own && !($index >= 1 && isSameGroup(comment, comments[$index - 1]))">
                                <img :src="comment.user.avatar_path" v-if="comment.user.avatar_path">
                                <span v-if="!comment.user.avatar_path">{{ comment.user.initial }}</span>
                            </span>
                        </div>

                        <div class="ix-comment-right">
                            <div class="ix-comment-right-top" v-if="isGroupStart($index) || comment.comment">
                                <span class="ix-comment-title">
                                    <template v-if="isGroupStart($index)">
                                        <span class="ix-comment-author">{{ comment.is_own ? 'Vous' : comment.user.fullname }}</span>
                                        <span class="ix-comment-date">{{ formatCommentDate(comment.created_at) }}</span>
                                    </template>
                                    <span class="ix-comment-reply" v-if="comment.comment"><i class="fas fa-reply"></i> en réponse à {{ comment.comment.user.fullname }}</span>
                                </span>
                            </div>

                            <div class="ix-comment-right-content">
                                <div class="ix-comment-messages" v-b-tooltip :title="comment.created_at | formatDate">
                                    <span class="ix-comment-quote" v-if="comment.comment" v-html="comment.comment.deleted_at ? 'Commentaire supprimé' : nl2br(truncate(comment.comment.message, 200))"></span>
                                    <span class="ix-comment-message" v-html="nl2br(comment.parsed_message)"></span>
                                </div>
                                <div class="ix-comment-actions">
                                    <button class="ix-comment-action" @click="replyComment(comment)"><i class="fas fa-reply"></i></button>
                                    <button class="ix-comment-action" @click="editComment(comment, $index)" v-if="(comment.is_own && $index == comments.length - 1)|| is_admin"><i class="fas fa-pen"></i></button>
                                    <button class="ix-comment-action" @click="deleteComment(comment, $index)" v-if="(comment.is_own && $index == comments.length - 1) || is_admin"><i class="fas fa-trash"></i></button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="ix-comments-form">
                <div class="form-group">
                    <textarea rows="1" placeholder="Écrivez un message .." class="form-control" v-model="comment.message" id="comment-message" @focus="adjustTextareaHeight()" @blur="disableTextareaHeight()" ref="commentMessage"></textarea>
                    <small class="text-muted form-text" v-if="comment.comment">
                        <i class="fas fa-reply"></i> En réponse à {{ comment.comment.user.fullname }}
                        <a @click="unlinkComment()" class="ix-link">retirer la réponse</a>
                    </small>
                </div>
                <div class="text-right">
                    <button class="ix-btn transparent" @click="cancelEvent()" v-if="comment.comment || comment.message || comment.id">Annuler</button>
                    <button class="ix-btn green" @click="postComment()" v-bind:class="{ disabled: !comment.message || loadingForm }">
                        <span v-if="!loadingForm && comment.id"><i class="far fa-pen"></i> Modifier</span>
                        <span v-if="!loadingForm && !comment.id"><i class="far fa-paper-plane"></i> Commenter</span>
                        <span v-if="loadingForm"><i class="far fa-circle-notch fa-spin"></i></span>
                    </button>
                </div>
            </div>
        </div>

        <!--
            Commentaires portés par les éléments liés, dans un bloc distinct et
            strictement consultable : aucune .ix-comment-actions, donc ni
            réponse, ni modification, ni suppression, et aucun champ de saisie.
            Chaque commentaire affiche son élément d'origine (type + référence,
            lien vers sa fiche) fourni par le serveur. Le bloc n'existe pas du
            tout s'il n'y a aucun commentaire lié.
            La carte réutilise la coquille .ix-documents de PROD (en-tête de bloc
            et lignes séparées par une bordure légère).
        -->
        <div class="ix-documents mt-3" v-if="linked && linkedComments.length > 0">
            <div class="ix-documents-header">
                <div class="ix-documents-header-left">
                    <i class="far fa-link"></i> Commentaires liés ({{ linkedComments.length }})
                </div>
            </div>

            <div class="px-3 pt-3 pb-2" v-if="(linkedPagination.last_page !== null && linkedPagination.last_page > linkedPagination.current_page) && !loadingLinked">
                <button class="ix-btn transparent ix-btn-block" @click="getLinkedData(linkedPagination.current_page + 1)">Afficher les commentaires liés précédents</button>
            </div>

            <div class="ix-comments-list boxed">
                <div class="ix-linked-comment" v-for="linkedComment in linkedComments" :key="'linked-' + linkedComment.id">
                    <div class="ix-linked-comment-author">
                        <span class="ix-avatar xsm">
                            <img :src="linkedComment.user.avatar_path" v-if="linkedComment.user.avatar_path">
                            <span v-if="!linkedComment.user.avatar_path">{{ linkedComment.user.initial }}</span>
                        </span>
                        <div>
                            <span class="ix-linked-comment-author-name">{{ linkedComment.user.fullname }}</span>
                            <span class="ix-text-secondary ix-text-small">{{ formatCommentDate(linkedComment.created_at) }}</span>
                        </div>
                    </div>

                    <div class="ix-linked-comment-body">
                        <div v-if="linkedComment.origin">
                            <a class="ix-tag ix-linked-comment-origin" v-if="linkedComment.origin.url" :href="linkedComment.origin.url">
                                <i class="far fa-file-alt"></i> {{ linkedComment.origin.name }} #{{ linkedComment.origin.reference }} <i class="far fa-external-link-alt"></i>
                            </a>
                            <span class="ix-tag ix-linked-comment-origin" v-else>
                                <i class="far fa-file-alt"></i> {{ linkedComment.origin.name }} #{{ linkedComment.origin.reference }}
                            </span>
                        </div>
<span class="ix-comment-quote" v-if="linkedComment.comment" v-html="linkedComment.comment.deleted_at ? 'Commentaire supprimé' : nl2br(truncate(linkedComment.comment.message, 200))"></span>
                        <div v-html="nl2br(linkedComment.parsed_message)"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue'
    import VueSwal from 'vue-swal'
    import autosize from "autosize/dist/autosize";

    Vue.use(VueSwal)

    export default {
        name: "Comments",
        props : {
            commentableType : String,
            commentableId : Number,
            order : String, // normal or reverse
            max : Number,
            admin : Number,
            url : "",
            restrictHeight: String,
            // Active le bloc « Commentaires liés ». Désactivé par défaut : les
            // emplacements qui ne la transmettent pas sont inchangés.
            linked : { type: Boolean, default: false }
        },
        watch: {
            commentableId: async function () {
                if (this.commentableId) {
                    await this.getData(1);
                }
            },
        },
        data() {
            return {
                comments : [],
                pagination: {
                    current_page: 1,
                    last_page: null,
                    per_page: this.max || 25,
                },
                loading: true,
                loadingForm: false,
                comment : {
                    id: null,
                    message: null,
                    comment: null,
                    index: null,
                },
                is_admin : this.admin || false,
                linkedComments : [],
                linkedPagination: {
                    current_page: 1,
                    last_page: null,
                    per_page: this.max || 25,
                },
                loadingLinked: false,
            }
        },
        methods : {
            // Messages consécutifs d'un même auteur, à moins d'une heure d'intervalle
            isSameGroup(comment, other){
                return comment.user_id === other.user_id
                    && Math.abs(this.moment(comment.created_at).diff(this.moment(other.created_at), 'minutes')) < 60;
            },
            // Premier message d'un groupe (les commentaires sont triés du plus récent au plus ancien)
            isGroupStart(index){
                return index == this.comments.length - 1 || !this.isSameGroup(this.comments[index], this.comments[index + 1]);
            },
            formatCommentDate(date){
                const value = this.moment(date);

                if (value.isSame(this.moment(), 'day')) {
                    return "Aujourd'hui à " + value.format('HH:mm');
                }

                if (value.isSame(this.moment().subtract(1, 'day'), 'day')) {
                    return 'Hier à ' + value.format('HH:mm');
                }

                return value.format(value.isSame(this.moment(), 'year') ? 'D MMMM [à] HH:mm' : 'D MMMM YYYY [à] HH:mm');
            },
            formatPagination(data){
                this.pagination.current_page = data.current_page
                this.pagination.last_page = data.last_page
            },
            async goToPage(number){
                if(number != this.pagination.current){
                    this.pagination.current = number
                    await this.getData();
                }
            },
            async postComment(){
                this.loadingForm = true;
                fetch(this.url + (this.comment.id ? '/' + this.comment.id : '') , {
                    ...{
                        method : this.comment.id ? "PUT" : "POST"
                    },
                    ...this.getHttpJsonRequestHeaders(),
                    ...{
                        body :  JSON.stringify({
                            comment_id : this.comment.comment ? this.comment.comment.id : null,
                            message : this.comment.message,
                            commentable_id : this.commentableId,
                            commentable_type : this.commentableType
                        })
                    }
                }).then((response) => {
                    this.loadingForm = false;
                    if (response.ok) {
                        response.json().then((data) => {
                            if (this.comment.index !== null) {
                                this.comments[this.comment.index] = data
                            } else {
                                if(this.comments.length == 0) {
                                    this.comments = [data]
                                } else {
                                    this.comments.unshift(data)
                                }

                                if (this.comments.length > this.pagination.per_page * this.pagination.current_page) {
                                    this.comments.splice(-1, 1)

                                    if (this.pagination.current_page == this.pagination.last_page) {
                                        this.pagination.last_page += 1
                                    }
                                }
                            }

                            this.cancelEvent()
                        })
                    }
                }).catch(error=> {
                    this.loadingForm = false;
                    console.log(error);
                })
            },
            async deleteComment(comment, index){
                this.$swal({
                    title : "Êtes vous sûr ?",
                    text : "Le commentaire sera définitivement supprimé",
                    icon : "warning",
                    dangerMode : true,
                    buttons : ["Annuler" , "Supprimer"]
                }).then((willDo) => {
                    if(willDo){
                        fetch(this.url + "/" + comment.id , {
                            ...{
                                method: 'DELETE'
                            },
                            ...this.getHttpJsonRequestHeaders()
                        }).then(async (response) => {
                            if (response.ok) {
                                this.comments.splice(index, 1)
                                this.getReplacementComment()
                            }
                        }).catch(error => {
                            console.log(error);
                        })
                    }
                });
            },
            async getReplacementComment(){
                fetch(this.url +  "?orderBy=!created_at&paginate=" + (this.comments.length + 1) +  "&commentable_type=" + this.commentableType +  "&commentable_id=" + this.commentableId +  "&page=" + 1, {
                    ...{
                        method: 'GET'
                    },
                    ...this.getHttpJsonRequestHeaders()
                }).then((response) => {
                    if (response.status == 200) {
                        response.json().then((data) => {
                            if (data.data) {
                                this.comments = data.data
                                this.pagination.last_page = Math.ceil(data.total / this.pagination.per_page)
                                this.pagination.current_page = Math.ceil(this.comments.length / this.pagination.per_page)
                            }
                        })
                    }
                }).catch(error => {
                    console.log(error)
                });
            },
            async editComment(comment, index){
                this.comment.id = comment.id
                this.comment.message = comment.message
                this.comment.comment = comment.comment
                this.comment.index = index

                this.scrollToForm()
            },
            async replyComment(comment){
                this.comment.comment = comment

                this.scrollToForm()
            },
            unlinkComment(){
                if (this.comment.comment) {
                    this.comment.comment = null
                }
            },
            cancelEvent(){
                this.comment = {
                    id: null,
                    message: null,
                    comment: null,
                    index: null
                }
            },
            adjustTextareaHeight() {
                var ta = document.getElementById('comment-message');
                ta.style.display = 'none';
                autosize(ta);
                ta.style.display = '';
                autosize.update(ta);
            },
            disableTextareaHeight() {
                var ta = document.getElementById('comment-message');
                autosize.destroy(ta);
            },
            scrollToForm() {
                document.getElementById('comment-message').scrollIntoView({ behavior: 'smooth', block: 'center' });
            },
            async getData(page){
                this.loading = true;
                fetch(this.url +  "?orderBy=!created_at&paginate=" + this.pagination.per_page +  "&commentable_type=" + this.commentableType +  "&commentable_id=" + this.commentableId +  "&page=" + page, {
                    ...{
                        method: 'GET'
                    },
                    ...this.getHttpJsonRequestHeaders()
                }).then((response) => {
                    this.loading = false;
                    if (response.status == 200) {
                        response.json().then((data) => {
                            if (data.data) {
                                this.comments = this.comments.concat(data.data);
                                this.formatPagination(data);
                            }
                        })
                    }
                }).catch(error => {
                    this.loading = false;
                    console.log(error)
                });
            },
            /**
             * Commentaires portés par les éléments liés.
             *
             * État et pagination entièrement séparés du fil propre : le
             * formulaire de saisie reste associé au seul élément courant, et la
             * pagination cumulative des commentaires propres n'est pas touchée.
             * Le serveur résout lui-même les éléments liés à partir du seul
             * élément courant, applique les droits et renvoie pour chaque
             * commentaire son origine (name, reference, url).
             */
            async getLinkedData(page){
                this.loadingLinked = true;
                fetch(this.url +  "?paginate=" + this.linkedPagination.per_page +  "&commentable_type=" + this.commentableType +  "&commentable_id=" + this.commentableId +  "&linked=1&page=" + page, {
                    ...{
                        method: 'GET'
                    },
                    ...this.getHttpJsonRequestHeaders()
                }).then((response) => {
                    this.loadingLinked = false;
                    if (response.status == 200) {
                        response.json().then((data) => {
                            if (data.data) {
                                this.linkedComments = this.linkedComments.concat(data.data);
                                this.linkedPagination.current_page = data.current_page
                                this.linkedPagination.last_page = data.last_page
                            }
                        })
                    }
                }).catch(error => {
                    this.loadingLinked = false;
                    console.log(error)
                });
            },
        },

        async mounted(){
            if (this.commentableId) {
                await this.getData(1);
            }

            if (this.linked && this.commentableId) {
                await this.getLinkedData(1);
            }
        }
    }
</script>
